import { useEffect, useMemo, useState } from "react";
import {
  X, Phone, MessageSquareText, Mail, Voicemail, StickyNote, ListTodo, Clock,
  Check, Bell, Trash2, ChevronDown, Loader2, Copy, CheckCircle2,
  Edit3, Save, Pin, Plus, Link2, AlertTriangle, Tag as TagIcon, BadgeCheck as BC,
} from "lucide-react";
import { api } from "../lib/api";
import AiAssistPanel from "./AiAssistPanel";
import CallLogQuickAction from "./CallLogQuickAction";
import VoiceNoteButton from "./VoiceNoteButton";
import WalkaroundButton from "./WalkaroundButton";

const STATUS_OPTIONS = [
  { v: "new",     label: "New",            color: "#6b7c93", desc: "Hasn't been touched yet" },
  { v: "working", label: "Working",        color: "#006DFF", desc: "Active conversation in progress" },
  { v: "active",  label: "Needs Follow-up", color: "#f59e0b", desc: "Waiting for you to circle back" },
  { v: "sold",    label: "Sold",           color: "#10a37f", desc: "Closed — announces on homepage" },
  { v: "lost",    label: "Lost",           color: "#ef4444", desc: "Decided not to buy from us" },
  { v: "bad",     label: "Bad",            color: "#475569", desc: "Test / spam / wrong number / no intent" },
];

const TASK_KIND_ICON = { call: Phone, text: MessageSquareText, email: Mail, appointment: Clock, other: ListTodo };

const TOUCHPOINT_BUTTONS = [
  { kind: "call",      label: "Called",       outcome: "spoke",          Icon: Phone },
  { kind: "voicemail", label: "Voicemail",    outcome: "left_voicemail", Icon: Voicemail },
  { kind: "text",      label: "Sent text",    outcome: null,             Icon: MessageSquareText },
  { kind: "email",     label: "Sent email",   outcome: null,             Icon: Mail },
  { kind: "note",      label: "Note",         outcome: null,             Icon: StickyNote },
];

/* Manager-only reassign control — small popover with a user picker. */
function ReassignBtn({ leadId, onDone }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [picking, setPicking] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open || users) return;
    api.get("/users").then(r => setUsers(r.data?.items || r.data || [])).catch(() => setUsers([]));
  }, [open, users]);
  const assign = async (target) => {
    setErr(""); setPicking(target?.id || "unassign");
    try {
      await api.post(`/dealer/lead/${leadId}/assign`,
        target ? { assigned_to_id: target.id } : {});
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Reassign failed";
      setErr(detail);
    } finally { setPicking(""); }
  };
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-[10px] text-blue-600 underline hover:text-blue-800"
        data-testid="lead-reassign-toggle"
      >Reassign</button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 bg-white border border-gray-200 rounded shadow-lg p-2 w-56 text-xs"
             data-testid="lead-reassign-popover">
          <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">Assign to</div>
          {users === null && <div className="text-gray-400">Loading…</div>}
          {users && users.length === 0 && <div className="text-gray-400">No users.</div>}
          <ul className="max-h-48 overflow-y-auto -mx-1">
            {(users || []).filter(u => u.active !== false).map(u => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => assign(u)}
                  disabled={picking === u.id}
                  className="w-full text-left px-2 py-1 rounded hover:bg-blue-50 disabled:opacity-50"
                  data-testid={`lead-reassign-${u.id}`}
                >
                  <div className="font-semibold text-gray-900">{u.name || u.email}</div>
                  <div className="text-[10px] text-gray-500">{u.role}</div>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t mt-2 pt-1 flex items-center justify-between">
            <button
              type="button"
              onClick={() => assign(null)}
              disabled={picking === "unassign"}
              className="text-[10px] text-red-600 hover:underline"
              data-testid="lead-reassign-unassign"
            >Unassign</button>
            <button type="button" onClick={() => setOpen(false)} className="text-[10px] text-gray-500">Cancel</button>
          </div>
          {err && <div className="text-[10px] text-red-600 mt-1" data-testid="lead-reassign-err">{err}</div>}
        </div>
      )}
    </span>
  );
}


function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function dueLabel(iso) {
  if (!iso) return "—";
  const diff = new Date(iso).getTime() - Date.now();
  const m = Math.round(diff / 60000);
  if (m < -60 * 24) return `${Math.abs(Math.round(m / 60 / 24))}d overdue`;
  if (m < 0) return `${Math.abs(Math.round(m / 60))}h overdue`;
  if (m < 60) return `in ${Math.max(1, m)}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `in ${h}h`;
  return `in ${Math.round(h / 24)}d`;
}

export default function LeadDetailDrawer({ leadId, onClose, onChanged }) {
  const [tab, setTab] = useState("timeline");   // timeline | tasks | notes | templates | trade
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState({ email: [], sms: [] });
  const [busy, setBusy] = useState("");
  const [statusOpen, setStatusOpen] = useState(false);

  // Editable header
  const [edit, setEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", phone: "", email: "", address: "", vehicle_title: "" });
  const [editErr, setEditErr] = useState("");

  // Notes tab
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  // Tags
  const [tagInput, setTagInput] = useState("");

  // Duplicates
  const [dupes, setDupes] = useState([]);

  // Win/Loss/Bad reasons (editable lists)
  const [reasons, setReasons] = useState({ win: [], loss: [], bad: [] });
  const [reasonOpen, setReasonOpen] = useState(null); // 'sold' | 'lost' | 'bad' | null
  const [reasonForm, setReasonForm] = useState({ reason: "", reason_detail: "", sale_price: "", vehicle_sold: "", announce_publicly: true });
  const [newReasonInput, setNewReasonInput] = useState("");

  // Link deal
  const [linkCode, setLinkCode] = useState("");
  const [linkErr, setLinkErr] = useState("");

  // Template preview state
  const [tplSelection, setTplSelection] = useState({ kind: "email", id: "" });
  const [rendered, setRendered] = useState(null);
  const [renderBusy, setRenderBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Note / touchpoint detail input
  const [touchDetail, setTouchDetail] = useState("");

  async function loadAll() {
    if (!leadId) return;
    setLoading(true);
    try {
      const [tl, tk, tp, nt, dp, rs] = await Promise.all([
        api.get(`/dealer/lead/${leadId}/timeline`),
        api.get(`/dealer/lead/${leadId}/tasks`),
        api.get("/templates"),
        api.get(`/dealer/lead/${leadId}/notes`),
        api.get(`/dealer/lead/${leadId}/duplicates`).catch(() => ({ data: { items: [] } })),
        api.get("/dealer/win-loss-reasons").catch(() => ({ data: { win: [], loss: [], bad: [] } })),
      ]);
      setData(tl.data);
      setTasks(tk.data.items || []);
      setTemplates({ email: tp.data.email || [], sms: tp.data.sms || [] });
      setNotes(nt.data.items || []);
      setDupes(dp.data.items || []);
      setReasons({ win: rs.data.win || [], loss: rs.data.loss || [], bad: rs.data.bad || [] });
      const L = tl.data.lead || {};
      setEditForm({
        name: L.name || "", phone: L.phone || "", email: L.email || "",
        address: L.address || "", vehicle_title: L.vehicle_title || "",
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [leadId]);

  const lead = data?.lead;
  const timeline = data?.timeline || [];

  async function changeStatus(v, extra = {}) {
    if (v === "sold" || v === "lost" || v === "bad") {
      // Open the reason dialog instead of immediate update
      setReasonOpen(v);
      setReasonForm({
        reason: "", reason_detail: "", sale_price: "",
        vehicle_sold: lead?.vehicle_title || "",
        announce_publicly: v === "sold",
      });
      setNewReasonInput("");
      setStatusOpen(false);
      return;
    }
    setStatusOpen(false);  // close picker immediately so the user sees feedback
    setBusy("status");
    try {
      await api.patch(`/dealer/lead/${leadId}/status`, { status: v, ...extra });
      await loadAll();
      onChanged?.();
    } catch (ex) {
      // Surface error so the user knows it failed instead of silently staying open
      window.alert(`Couldn't change status: ${ex?.response?.data?.detail || "unknown error"}`);
    } finally {
      setBusy("");
    }
  }

  async function addCustomReason() {
    const v = newReasonInput.trim();
    if (!v) return;
    const key = reasonOpen === "sold" ? "win" : reasonOpen === "lost" ? "loss" : "bad";
    if ((reasons[key] || []).includes(v)) { setNewReasonInput(""); setReasonForm((s) => ({ ...s, reason: v })); return; }
    const next = [...(reasons[key] || []), v];
    await api.patch("/dealer/win-loss-reasons", { [key]: next });
    setReasons((r) => ({ ...r, [key]: next }));
    setReasonForm((s) => ({ ...s, reason: v }));
    setNewReasonInput("");
  }

  async function submitReason() {
    if (!reasonForm.reason) { return; }
    setBusy("status");
    try {
      await api.patch(`/dealer/lead/${leadId}/close`, {
        status: reasonOpen,
        reason: reasonForm.reason,
        reason_detail: reasonForm.reason_detail || null,
        sale_price: reasonForm.sale_price ? Number(reasonForm.sale_price) : null,
        vehicle_sold: reasonForm.vehicle_sold || null,
        announce_publicly: !!reasonForm.announce_publicly,
      });
      setReasonOpen(null);
      await loadAll();
      onChanged?.();
    } finally {
      setBusy("");
    }
  }

  async function saveEdits() {
    setBusy("edit"); setEditErr("");
    try {
      await api.patch(`/dealer/lead/${leadId}`, editForm);
      setEdit(false);
      await loadAll();
      onChanged?.();
    } catch (ex) {
      setEditErr(ex?.response?.data?.detail || "Couldn't save changes.");
    } finally {
      setBusy("");
    }
  }

  async function addNote() {
    if (!newNote.trim()) return;
    setBusy("note");
    try {
      await api.post(`/dealer/lead/${leadId}/notes`, { note: newNote.trim(), pinned: false });
      setNewNote("");
      await loadAll();
    } finally { setBusy(""); }
  }
  async function updateNote(noteId, patch) {
    await api.patch(`/dealer/lead/${leadId}/notes/${noteId}`, patch);
    await loadAll();
  }
  async function deleteNote(noteId) {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/dealer/lead/${leadId}/notes/${noteId}`);
    await loadAll();
  }

  async function addTag() {
    const v = tagInput.trim();
    if (!v) return;
    const existing = lead?.tags || [];
    if (existing.includes(v)) { setTagInput(""); return; }
    await api.patch(`/dealer/lead/${leadId}/tags`, { tags: [...existing, v] });
    setTagInput("");
    await loadAll();
    onChanged?.();
  }
  async function removeTag(t) {
    const existing = lead?.tags || [];
    await api.patch(`/dealer/lead/${leadId}/tags`, { tags: existing.filter((x) => x !== t) });
    await loadAll();
    onChanged?.();
  }

  async function linkDeal() {
    if (!linkCode.trim()) return;
    setBusy("link"); setLinkErr("");
    try {
      await api.post(`/dealer/lead/${leadId}/link-deal`, { deal_code: linkCode.trim().toUpperCase() });
      setLinkCode("");
      await loadAll();
      onChanged?.();
    } catch (ex) {
      setLinkErr(ex?.response?.data?.detail || "Couldn't link deal.");
    } finally { setBusy(""); }
  }

  async function mergeFrom(srcId) {
    if (!window.confirm("Merge this duplicate's history INTO the current lead and delete the duplicate?")) return;
    await api.post(`/dealer/lead/${leadId}/merge-from/${srcId}`);
    await loadAll();
    onChanged?.();
  }

  async function logTouchpoint(btn) {
    setBusy(btn.kind);
    try {
      await api.post(`/dealer/lead/${leadId}/touchpoint`, {
        kind: btn.kind,
        outcome: btn.outcome,
        detail: touchDetail.trim() || null,
      });
      setTouchDetail("");
      await loadAll();
      onChanged?.();
    } finally {
      setBusy("");
    }
  }

  async function patchTask(id, body) {
    await api.patch(`/dealer/tasks/${id}`, body);
    await loadAll();
  }

  async function renderTemplate(id, kind) {
    setRenderBusy(true);
    setRendered(null);
    setCopied(false);
    try {
      const { data: r } = await api.post("/templates/render", { template_id: id, lead_id: leadId });
      setRendered({ ...r.rendered, kind });
    } finally {
      setRenderBusy(false);
    }
  }

  const grouped = useMemo(() => {
    const byCat = {};
    (templates[tplSelection.kind] || []).forEach((t) => {
      (byCat[t.category] ||= []).push(t);
    });
    return byCat;
  }, [templates, tplSelection.kind]);

  function copyBody() {
    if (!rendered) return;
    const text = rendered.subject ? `Subject: ${rendered.subject}\n\n${rendered.body}` : rendered.body;
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  function openMailOrSms() {
    if (!rendered || !lead) return;
    if (rendered.kind === "email") {
      const to = encodeURIComponent(lead.email || "");
      const subject = encodeURIComponent(rendered.subject || "");
      const body = encodeURIComponent(rendered.body || "");
      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    } else {
      const phone = (lead.phone || "").replace(/[^\d+]/g, "");
      const body = encodeURIComponent(rendered.body || "");
      window.location.href = `sms:${phone}?body=${body}`;
    }
    // Log the touchpoint immediately so it shows on the timeline
    api.post(`/dealer/lead/${leadId}/touchpoint`, {
      kind: rendered.kind === "email" ? "email" : "text",
      template_id: rendered.id,
      detail: `Used template: ${rendered.label}`,
    }).then(() => loadAll());
  }

  if (!leadId) return null;
  const status = lead?.status || "new";
  const statusOpt = STATUS_OPTIONS.find((s) => s.v === status) || STATUS_OPTIONS[0];

  return (
    <div className="fixed inset-0 z-[90] flex justify-end" role="dialog" aria-modal="true" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside className="relative bg-white w-full sm:w-[640px] h-screen overflow-y-auto shadow-2xl flex flex-col">
        {loading ? (
          <div className="p-10 flex items-center gap-2 text-[var(--ssc-ink-2)]">
            <Loader2 className="animate-spin" /> Loading lead…
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="sticky top-0 z-10 bg-white border-b border-[#D9E1EA] p-4 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black uppercase tracking-wider text-[#6b7c93] flex items-center gap-2">
                  <span>Lead · {lead?.lead_source || lead?.lead_type || "website"}</span>
                  {!edit && (
                    <button type="button" onClick={() => setEdit(true)} className="text-[var(--ssc-blue)] hover:underline inline-flex items-center gap-1 text-xs normal-case font-bold tracking-normal" data-testid="lead-edit-toggle">
                      <Edit3 size={12} /> Edit
                    </button>
                  )}
                </div>
                {!edit ? (
                  <>
                    <h2 className="text-xl font-black text-[var(--ssc-navy)] truncate" data-testid="lead-drawer-name">
                      {lead?.name || "Anonymous"}
                    </h2>
                    <div className="text-sm text-[#475569] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                      {lead?.phone && <a href={`tel:${lead.phone}`} className="text-[var(--ssc-blue)] font-bold">{lead.phone}</a>}
                      {lead?.phone && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const r = await api.get(`/dealer/lead/${leadId}/sms-link?template=intro`);
                              window.location.href = r.data?.sms_url;
                            } catch (e) {
                              alert(e?.response?.data?.detail || "Could not build SMS link.");
                            }
                          }}
                          className="text-[11px] px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1"
                          title="Open SMS app pre-filled with an intro message"
                          data-testid="lead-sms-intro"
                        >
                          Text intro
                        </button>
                      )}
                      {lead?.email && <a href={`mailto:${lead.email}`} className="text-[var(--ssc-blue)] font-bold">{lead.email}</a>}
                    </div>
                    {lead?.assigned_to_name && (
                      <div className="text-[11px] text-[#6b7c93] mt-1 inline-flex items-center gap-2" data-testid="lead-assigned-row">
                        <span>Assigned: <strong className="text-[var(--ssc-navy)]">{lead.assigned_to_name}</strong></span>
                        <ReassignBtn leadId={leadId} onDone={loadAll} />
                      </div>
                    )}
                    {!lead?.assigned_to_name && (
                      <div className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-2" data-testid="lead-unassigned-row">
                        <span>Unassigned</span>
                        <ReassignBtn leadId={leadId} onDone={loadAll} />
                      </div>
                    )}
                    {lead?.address && <div className="text-xs text-[#6b7c93] mt-1">{lead.address}</div>}
                    {lead?.vehicle_title && <div className="text-sm font-bold text-[var(--ssc-navy)] mt-1">{lead.vehicle_title}</div>}
                  </>
                ) : (
                  <div className="space-y-2 mt-1" data-testid="lead-edit-form">
                    <input className="w-full px-3 py-1.5 rounded border border-[#D9E1EA] font-extrabold text-[var(--ssc-navy)]" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" data-testid="lead-edit-name" />
                    <div className="grid grid-cols-2 gap-2">
                      <input className="px-3 py-1.5 rounded border border-[#D9E1EA] text-sm" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Phone" data-testid="lead-edit-phone" />
                      <input className="px-3 py-1.5 rounded border border-[#D9E1EA] text-sm" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" data-testid="lead-edit-email" />
                    </div>
                    <input className="w-full px-3 py-1.5 rounded border border-[#D9E1EA] text-sm" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} placeholder="Address" data-testid="lead-edit-address" />
                    <input className="w-full px-3 py-1.5 rounded border border-[#D9E1EA] text-sm" value={editForm.vehicle_title} onChange={(e) => setEditForm({ ...editForm, vehicle_title: e.target.value })} placeholder="Vehicle of interest" data-testid="lead-edit-vehicle" />
                    <div className="flex gap-2">
                      <button type="button" onClick={saveEdits} disabled={busy === "edit"} className="ssc-btn ssc-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="lead-edit-save">
                        {busy === "edit" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                      </button>
                      <button type="button" onClick={() => { setEdit(false); setEditErr(""); }} className="text-xs text-[#6b7c93] hover:text-[var(--ssc-navy)] px-2" data-testid="lead-edit-cancel">Cancel</button>
                    </div>
                    {editErr && <div className="text-xs text-red-600">{editErr}</div>}
                  </div>
                )}
                {/* Tags row */}
                {!edit && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="lead-tags">
                    {(lead?.tags || []).map((t) => (
                      <span key={t} className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] border border-[var(--ssc-blue)]/30 inline-flex items-center gap-1">
                        <TagIcon size={9} /> {t}
                        <button type="button" onClick={() => removeTag(t)} className="hover:text-red-600 ml-0.5" data-testid={`lead-tag-remove-${t}`}>×</button>
                      </span>
                    ))}
                    <input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                      placeholder="+ tag"
                      className="text-[10px] px-2 py-0.5 rounded-full border border-[#D9E1EA] w-16 focus:w-24 transition-all"
                      data-testid="lead-tag-input"
                    />
                  </div>
                )}
                {lead?.deal_code && (
                  <div className="mt-1 text-xs text-[var(--ssc-blue)] font-bold inline-flex items-center gap-1">
                    <Link2 size={11} /> Linked deal: <span className="font-mono">{lead.deal_code}</span>
                  </div>
                )}
              </div>
              <button onClick={onClose} className="text-[#6b7c93] hover:text-[var(--ssc-navy)] p-1" data-testid="lead-drawer-close">
                <X size={22} />
              </button>
            </header>

            {/* Quick-actions strip — call-log, voice note, walkaround */}
            {leadId && (
              <div className="px-4 py-2 bg-[var(--ssc-paper)] border-b border-[#D9E1EA] flex items-start gap-2 flex-wrap" data-testid="lead-quick-actions">
                <CallLogQuickAction leadId={leadId} onLogged={loadAll} />
                <VoiceNoteButton leadId={leadId} onSaved={loadAll} />
                <WalkaroundButton leadId={leadId} onSaved={loadAll} />
              </div>
            )}

            {/* Duplicate warning */}
            {dupes.length > 0 && (
              <section className="px-4 py-2 bg-amber-50 border-b border-amber-200" data-testid="lead-dupe-warning">
                <div className="text-xs flex items-start gap-2">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-extrabold text-amber-900">
                      Possible duplicate{dupes.length > 1 ? "s" : ""} ({dupes.length})
                    </div>
                    <ul className="mt-1 space-y-1">
                      {dupes.slice(0, 3).map((d) => (
                        <li key={d.id} className="flex items-center gap-2 text-amber-900">
                          <span className="truncate">
                            {d.name || "Anonymous"} · {(d.match_reasons || []).join(" + ")} match · {new Date(d.created_at).toLocaleDateString()}
                          </span>
                          <button type="button" onClick={() => mergeFrom(d.id)} className="text-xs font-bold text-[var(--ssc-blue)] hover:underline" data-testid={`lead-merge-${d.id}`}>
                            Merge into this
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {/* AI Assist panel — summary, score, draft */}
            {leadId && <AiAssistPanel lead={lead} leadId={leadId} onRefresh={loadAll} />}

            {/* Trade vehicle details (VIN decode + condition + damage) */}
            {(lead?.trade_vehicle || lead?.trade_estimate) && (
              <TradeDetails veh={lead?.trade_vehicle} est={lead?.trade_estimate_full || {}} />
            )}

            {/* Protection products selected / declined on Build My Own Deal */}
            {(lead?.protection_selected?.length > 0 || lead?.protection_declined?.length > 0) && (
              <ProtectionSummary lead={lead} />
            )}

            {/* Status selector */}
            <section className="p-4 border-b border-[#D9E1EA]">
              <label className="text-xs font-black uppercase tracking-wider text-[#6b7c93] block mb-1.5">Status</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setStatusOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border-2 font-extrabold transition"
                  style={{ borderColor: statusOpt.color, color: statusOpt.color, background: `${statusOpt.color}10` }}
                  data-testid="lead-status-pill"
                >
                  <span>{statusOpt.label}</span>
                  <span className="text-xs font-bold text-[#6b7c93]">{statusOpt.desc}</span>
                  <ChevronDown size={16} />
                </button>
                {statusOpen && (
                  <div className="absolute z-20 mt-1 left-0 right-0 bg-white border border-[#D9E1EA] rounded-xl shadow-xl overflow-hidden" data-testid="lead-status-menu">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => changeStatus(s.v)}
                        disabled={busy === "status"}
                        className="w-full text-left px-3 py-2.5 flex items-start gap-2 hover:bg-[#F4F7FB] border-b last:border-b-0 border-[#EEF2F7]"
                        data-testid={`lead-status-set-${s.v}`}
                      >
                        <span className="shrink-0 w-3 h-3 mt-1.5 rounded-full" style={{ background: s.color }} />
                        <span className="flex-1">
                          <div className="font-extrabold text-[var(--ssc-navy)]">{s.label}</div>
                          <div className="text-xs text-[#6b7c93]">{s.desc}</div>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Touchpoint quick-log */}
            <section className="p-4 border-b border-[#D9E1EA]">
              <label className="text-xs font-black uppercase tracking-wider text-[#6b7c93] block mb-1.5">
                Quick log
              </label>
              <input
                value={touchDetail}
                onChange={(e) => setTouchDetail(e.target.value)}
                placeholder="Optional detail — e.g. 'Spoke 5 min, wants to come Sat'"
                className="w-full px-3 py-2 rounded-lg border border-[#D9E1EA] text-sm mb-2"
                data-testid="touchpoint-detail"
              />
              <div className="flex gap-1.5 flex-wrap">
                {TOUCHPOINT_BUTTONS.map(({ kind, label, Icon, outcome }) => (
                  <button
                    key={kind + (outcome || "")}
                    type="button"
                    onClick={() => logTouchpoint({ kind, outcome })}
                    disabled={busy === kind}
                    className="ssc-btn ssc-btn-secondary text-xs px-3 py-2"
                    data-testid={`touchpoint-${kind}`}
                  >
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>
            </section>

            {/* Tabs */}
            <nav className="px-4 pt-3 border-b border-[#D9E1EA] flex gap-1 flex-wrap">
              {[
                { v: "timeline",  label: "Timeline",  icon: Clock },
                { v: "tasks",     label: `Tasks (${tasks.filter(t => t.status === "open").length})`, icon: ListTodo },
                { v: "notes",     label: `Notes (${notes.length})`, icon: StickyNote },
                { v: "templates", label: "Templates", icon: Mail },
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setTab(t.v)}
                  className={`px-3 py-2 text-sm font-extrabold border-b-2 -mb-px transition ${tab === t.v ? "border-[var(--ssc-blue)] text-[var(--ssc-blue)]" : "border-transparent text-[#6b7c93] hover:text-[var(--ssc-navy)]"}`}
                  data-testid={`drawer-tab-${t.v}`}
                >
                  <t.icon size={14} className="inline -mt-0.5 mr-1" />{t.label}
                </button>
              ))}
            </nav>

            {/* Tab content */}
            <div className="flex-1 p-4 space-y-3">
              {tab === "timeline" && (
                <ul className="space-y-2" data-testid="lead-timeline">
                  {timeline.slice().reverse().map((e) => (
                    <li key={e.id} className="flex gap-2 text-sm">
                      <span className="shrink-0 w-2 h-2 mt-2 rounded-full bg-[var(--ssc-blue)]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[var(--ssc-navy)]">{e.detail}</div>
                        <div className="text-xs text-[#6b7c93]">{e.by} · {timeAgo(e.at)}</div>
                      </div>
                    </li>
                  ))}
                  {timeline.length === 0 && <li className="text-sm text-[#6b7c93]">No activity yet.</li>}
                </ul>
              )}

              {tab === "tasks" && (
                <ul className="space-y-2" data-testid="lead-tasks">
                  {tasks.length === 0 && (
                    <li className="text-sm text-[#6b7c93]">
                      No tasks yet. They'll auto-generate the moment you move this lead to <strong>Working</strong>.
                    </li>
                  )}
                  {tasks.map((t) => {
                    const Icon = TASK_KIND_ICON[t.kind] || ListTodo;
                    const isDone = t.status === "done";
                    const isDismissed = t.status === "dismissed";
                    const isOverdue = !isDone && !isDismissed && new Date(t.due_at).getTime() < Date.now();
                    return (
                      <li
                        key={t.id}
                        className={`rounded-xl border p-3 flex items-start gap-2 ${isOverdue ? "border-[#ef4444] bg-[#FFF4F4]" : "border-[#D9E1EA] bg-white"} ${isDone || isDismissed ? "opacity-50" : ""}`}
                        data-testid={`task-${t.id}`}
                      >
                        <Icon size={16} className="shrink-0 mt-0.5 text-[var(--ssc-blue)]" />
                        <div className="flex-1 min-w-0">
                          <div className={`font-extrabold text-[var(--ssc-navy)] ${isDone || isDismissed ? "line-through" : ""}`}>
                            {t.title}
                          </div>
                          <div className="text-xs">
                            <span className={isOverdue ? "text-[#ef4444] font-black" : "text-[#6b7c93]"}>
                              {isDone ? "Done" : isDismissed ? "Dismissed" : `Due ${dueLabel(t.due_at)}`}
                            </span>
                          </div>
                        </div>
                        {!isDone && !isDismissed && (
                          <div className="flex gap-1 shrink-0">
                            <button type="button" onClick={() => patchTask(t.id, { status: "done" })} className="p-1 text-[#10a37f] hover:bg-[#E6FFF4] rounded" title="Mark done" data-testid={`task-done-${t.id}`}>
                              <Check size={16} />
                            </button>
                            <button type="button" onClick={() => patchTask(t.id, { status: "snoozed", snooze_days: 1 })} className="p-1 text-[var(--ssc-blue)] hover:bg-[#E8F0FF] rounded" title="Snooze 1 day" data-testid={`task-snooze-${t.id}`}>
                              <Bell size={16} />
                            </button>
                            <button type="button" onClick={() => patchTask(t.id, { status: "dismissed" })} className="p-1 text-[#ef4444] hover:bg-[#FFF4F4] rounded" title="Dismiss" data-testid={`task-dismiss-${t.id}`}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {tab === "notes" && (
                <div className="space-y-3" data-testid="lead-notes">
                  <div className="bg-[var(--ssc-paper)] rounded-xl p-3 border border-[#D9E1EA]">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note (e.g. 'Wants to come Sat morning, financing pre-approved via 5/3 Bank')…"
                      className="w-full px-3 py-2 rounded-lg border border-[#D9E1EA] text-sm min-h-[70px] bg-white"
                      data-testid="lead-note-input"
                    />
                    <div className="mt-2 flex justify-end">
                      <button type="button" onClick={addNote} disabled={busy === "note" || !newNote.trim()}
                              className="ssc-btn ssc-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"
                              data-testid="lead-note-add">
                        {busy === "note" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add Note
                      </button>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {notes.length === 0 && <li className="text-sm text-[#6b7c93] px-1">No notes yet. Pin the important stuff.</li>}
                    {notes.map((n) => (
                      <li key={n.id} className={`rounded-xl border p-3 ${n.pinned ? "border-amber-300 bg-amber-50" : "border-[#D9E1EA] bg-white"}`} data-testid={`lead-note-${n.id}`}>
                        {editingNoteId === n.id ? (
                          <div>
                            <textarea
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              className="w-full px-2 py-1 rounded border border-[#D9E1EA] text-sm bg-white min-h-[60px]"
                              data-testid={`lead-note-edit-input-${n.id}`}
                            />
                            <div className="mt-1 flex gap-2 justify-end">
                              <button type="button" onClick={async () => { await updateNote(n.id, { note: editingNoteText }); setEditingNoteId(null); }} className="text-xs font-bold text-[var(--ssc-blue)] hover:underline" data-testid={`lead-note-save-${n.id}`}>Save</button>
                              <button type="button" onClick={() => setEditingNoteId(null)} className="text-xs text-[#6b7c93] hover:text-[var(--ssc-navy)]">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="text-sm text-[var(--ssc-navy)] whitespace-pre-wrap">{n.note}</div>
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <div className="text-[10px] text-[#6b7c93] uppercase tracking-wider font-bold">
                                {n.by} · {new Date(n.at).toLocaleString()}
                                {n.updated_at && n.updated_at !== n.at && <span> · edited</span>}
                              </div>
                              <div className="flex gap-1">
                                <button type="button" onClick={() => updateNote(n.id, { pinned: !n.pinned })} className={`p-1 rounded ${n.pinned ? "text-amber-600 bg-amber-100" : "text-[#6b7c93] hover:bg-[#F4F7FB]"}`} title={n.pinned ? "Unpin" : "Pin"} data-testid={`lead-note-pin-${n.id}`}>
                                  <Pin size={13} />
                                </button>
                                <button type="button" onClick={() => { setEditingNoteId(n.id); setEditingNoteText(n.note); }} className="p-1 text-[#6b7c93] hover:bg-[#F4F7FB] rounded" title="Edit" data-testid={`lead-note-edit-${n.id}`}>
                                  <Edit3 size={13} />
                                </button>
                                <button type="button" onClick={() => deleteNote(n.id)} className="p-1 text-[#ef4444] hover:bg-[#FFF4F4] rounded" title="Delete" data-testid={`lead-note-delete-${n.id}`}>
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>

                  {/* Link deal section */}
                  <div className="rounded-xl border border-[#D9E1EA] bg-white p-3" data-testid="lead-deal-link">
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] mb-1 flex items-center gap-1">
                      <Link2 size={11} /> Link Deal (BYOD code)
                    </div>
                    {lead?.deal_code ? (
                      <div className="text-sm text-[var(--ssc-navy)] font-bold">
                        Linked: <span className="font-mono">{lead.deal_code}</span>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          value={linkCode}
                          onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                          placeholder="e.g. SSC-AB7K"
                          className="flex-1 px-2 py-1 rounded border border-[#D9E1EA] text-sm font-mono"
                          data-testid="lead-link-input"
                        />
                        <button type="button" onClick={linkDeal} disabled={busy === "link" || !linkCode.trim()} className="ssc-btn ssc-btn-secondary text-xs px-3" data-testid="lead-link-submit">
                          {busy === "link" ? <Loader2 size={13} className="animate-spin" /> : "Link"}
                        </button>
                      </div>
                    )}
                    {linkErr && <div className="text-xs text-red-600 mt-1">{linkErr}</div>}
                  </div>
                </div>
              )}

              {tab === "templates" && (
                <div data-testid="lead-templates">
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setTplSelection({ kind: "email", id: "" })}
                      className={`px-3 py-1.5 rounded-full text-xs font-extrabold border ${tplSelection.kind === "email" ? "bg-[var(--ssc-blue)] text-white border-[var(--ssc-blue)]" : "bg-white text-[var(--ssc-navy)] border-[#D9E1EA]"}`}
                      data-testid="tpl-kind-email"
                    >
                      <Mail size={12} className="inline -mt-0.5 mr-1" /> Email
                    </button>
                    <button
                      type="button"
                      onClick={() => setTplSelection({ kind: "sms", id: "" })}
                      className={`px-3 py-1.5 rounded-full text-xs font-extrabold border ${tplSelection.kind === "sms" ? "bg-[var(--ssc-blue)] text-white border-[var(--ssc-blue)]" : "bg-white text-[var(--ssc-navy)] border-[#D9E1EA]"}`}
                      data-testid="tpl-kind-sms"
                    >
                      <MessageSquareText size={12} className="inline -mt-0.5 mr-1" /> Text
                    </button>
                  </div>

                  <select
                    value={tplSelection.id}
                    onChange={(e) => { setTplSelection((s) => ({ ...s, id: e.target.value })); if (e.target.value) renderTemplate(e.target.value, tplSelection.kind); }}
                    className="w-full px-3 py-2 rounded-lg border border-[#D9E1EA] text-sm font-bold text-[var(--ssc-navy)] mb-3"
                    data-testid="tpl-select"
                  >
                    <option value="">Pick a template…</option>
                    {Object.entries(grouped).map(([cat, list]) => (
                      <optgroup key={cat} label={cat}>
                        {list.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </optgroup>
                    ))}
                  </select>

                  {renderBusy && (
                    <div className="text-sm text-[#6b7c93] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Rendering…</div>
                  )}

                  {rendered && (
                    <div className="rounded-xl border border-[#D9E1EA] bg-[#F4F7FB] p-3 mt-2" data-testid="tpl-preview">
                      {rendered.subject && (
                        <div className="text-xs text-[#6b7c93] mb-1">Subject</div>
                      )}
                      {rendered.subject && (
                        <div className="font-extrabold text-[var(--ssc-navy)] mb-3" data-testid="tpl-subject">{rendered.subject}</div>
                      )}
                      <div className="text-xs text-[#6b7c93] mb-1">Body — edit anywhere in your mail/SMS app</div>
                      <textarea
                        value={rendered.body}
                        onChange={(e) => setRendered((r) => ({ ...r, body: e.target.value }))}
                        rows={Math.min(14, (rendered.body.match(/\n/g) || []).length + 3)}
                        className="w-full px-3 py-2 rounded-lg border border-[#D9E1EA] text-sm font-mono leading-relaxed bg-white"
                        data-testid="tpl-body"
                      />
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" onClick={openMailOrSms} className="ssc-btn ssc-btn-primary text-sm" data-testid="tpl-open">
                          {rendered.kind === "email" ? <><Mail size={14} /> Open in mail app</> : <><MessageSquareText size={14} /> Open in messages</>}
                        </button>
                        <button type="button" onClick={copyBody} className="ssc-btn ssc-btn-secondary text-sm" data-testid="tpl-copy">
                          {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Win/Loss/Bad reason dialog */}
            {reasonOpen && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50 p-4" data-testid="win-loss-modal">
                <div className="bg-white rounded-2xl w-full max-w-md p-5">
                  <div className="text-xs font-extrabold uppercase tracking-widest text-[#6b7c93]">Mark {reasonOpen}</div>
                  <h3 className="text-lg font-black text-[var(--ssc-navy)] mt-1">
                    {reasonOpen === "sold" ? "Why did they buy?"
                     : reasonOpen === "lost" ? "Why was this lead lost?"
                     : "Why is this a bad lead?"}
                  </h3>
                  <div className="mt-3 space-y-2">
                    <select className="w-full px-3 py-2 rounded border border-[#D9E1EA] text-sm font-bold" value={reasonForm.reason}
                            onChange={(e) => setReasonForm({ ...reasonForm, reason: e.target.value })} data-testid="reason-select">
                      <option value="">Pick a reason…</option>
                      {(reasonOpen === "sold" ? reasons.win : reasonOpen === "lost" ? reasons.loss : reasons.bad).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    {/* Add custom reason inline */}
                    <div className="flex gap-1.5">
                      <input
                        value={newReasonInput}
                        onChange={(e) => setNewReasonInput(e.target.value)}
                        placeholder="+ Add custom reason (e.g. 'No intent to buy')"
                        className="flex-1 px-3 py-2 rounded border border-[#D9E1EA] text-sm"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomReason(); } }}
                        data-testid="reason-new-input"
                      />
                      <button type="button" onClick={addCustomReason} disabled={!newReasonInput.trim()}
                              className="ssc-btn ssc-btn-secondary text-xs px-3" data-testid="reason-new-add">
                        Add
                      </button>
                    </div>
                    <input className="w-full px-3 py-2 rounded border border-[#D9E1EA] text-sm" value={reasonForm.reason_detail}
                           onChange={(e) => setReasonForm({ ...reasonForm, reason_detail: e.target.value })}
                           placeholder="Optional detail" data-testid="reason-detail" />
                    {reasonOpen === "sold" && (
                      <>
                        <input className="w-full px-3 py-2 rounded border border-[#D9E1EA] text-sm" value={reasonForm.vehicle_sold}
                               onChange={(e) => setReasonForm({ ...reasonForm, vehicle_sold: e.target.value })}
                               placeholder="Vehicle they bought" data-testid="reason-vehicle" />
                        <input className="w-full px-3 py-2 rounded border border-[#D9E1EA] text-sm" inputMode="numeric" value={reasonForm.sale_price}
                               onChange={(e) => setReasonForm({ ...reasonForm, sale_price: e.target.value })}
                               placeholder="Sale price (optional)" data-testid="reason-price" />
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={reasonForm.announce_publicly}
                                 onChange={(e) => setReasonForm({ ...reasonForm, announce_publicly: e.target.checked })}
                                 data-testid="reason-announce" />
                          Announce on homepage (first name + last initial)
                        </label>
                      </>
                    )}
                  </div>
                  <div className="mt-4 flex gap-2 justify-end">
                    <button type="button" onClick={() => setReasonOpen(null)} className="text-sm text-[#6b7c93] hover:text-[var(--ssc-navy)] px-3 py-1.5" data-testid="reason-cancel">Cancel</button>
                    <button type="button" onClick={submitReason} disabled={busy === "status" || !reasonForm.reason}
                            className="ssc-btn ssc-btn-primary text-sm px-3 py-1.5" data-testid="reason-submit">
                      {busy === "status" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirm {reasonOpen}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}


// ============================================================
// Trade Vehicle Details (VIN decode, condition, damage)
// ============================================================
function TradeDetails({ veh, est }) {
  const v = veh || {};
  if (!v.vin && !v.year && !v.make && !v.engine && !v.cab_type && !v.body_damage) return null;

  const decoded = v.decoded || {};
  const damaged = Object.entries(v.body_damage || {}).filter(([, lvl]) => lvl && lvl !== "none");
  const interiorIssues = [
    v.interior_rips_tears && "Rips/Tears",
    v.interior_stains && "Stains",
    v.interior_rust && "Rust",
  ].filter(Boolean);
  const detected = v.detected_options || [];
  const confirmed = v.confirmed_options || [];

  return (
    <section className="p-4 border-b border-[#D9E1EA] bg-[#F8FAFD]" data-testid="lead-trade-details">
      <div className="text-xs font-black uppercase tracking-wider text-[#6b7c93] mb-2">
        Trade Vehicle Details
      </div>

      <div className="rounded-xl border border-[#D9E1EA] bg-white p-3">
        <div className="text-sm font-black text-[var(--ssc-navy)] mb-1">
          {[v.year, v.make, v.model, v.trim].filter(Boolean).join(" ") || "Vehicle"}
        </div>
        {v.vin && (
          <div className="text-xs text-[#475569] mb-2">
            <span className="font-bold text-[#6b7c93]">VIN:</span>{" "}
            <span className="font-mono font-bold text-[var(--ssc-navy)]" data-testid="lead-trade-vin">{v.vin}</span>
            {v.vin_provider && <span className="ml-2 text-[10px] uppercase font-extrabold tracking-widest text-[#6b7c93]">via {v.vin_provider}</span>}
          </div>
        )}
        <DLGrid items={[
          ["Mileage", v.mileage ? `${Number(v.mileage).toLocaleString()} mi` : null],
          ["Condition", v.condition],
          ["Engine", v.engine || (decoded.displacement_l ? `${decoded.displacement_l}L${decoded.engine_cyl ? ` ${decoded.engine_cyl}-cyl` : ""}` : null)],
          ["Transmission", v.transmission || decoded.transmission_style],
          ["Drivetrain", v.drivetrain || decoded.drivetrain],
          ["Cab Type", v.cab_type || decoded.cab_type],
          ["Bed Size", v.bed_size || decoded.bed_type],
          ["Exterior", v.exterior_color],
          ["Interior", [v.interior_color, v.interior_material].filter(Boolean).join(" · ") || null],
          ["Title", v.title_status],
          ["Owners", v.owners],
          ["Accidents", v.accidents],
          ["MSRP (orig.)", v.vin_msrp ? `$${Number(v.vin_msrp).toLocaleString()}` : null],
          ["Confidence", v.vin_confidence ? `${Math.round(v.vin_confidence * 100)}%` : null],
        ]} />
        {v.vin_window_sticker_url && (
          <a href={v.vin_window_sticker_url} target="_blank" rel="noopener noreferrer"
             className="mt-2 inline-block text-xs font-bold text-[var(--ssc-blue)] underline">
            View original window sticker
          </a>
        )}
      </div>

      {(damaged.length > 0 || interiorIssues.length > 0) && (
        <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="lead-trade-damage">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-700 mb-1">
            Damage Reported by Customer
          </div>
          {damaged.length > 0 && (
            <ul className="text-xs text-[var(--ssc-navy)] space-y-0.5">
              {damaged.map(([panel, lvl]) => (
                <li key={panel}>
                  <span className="font-bold">{panel.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}:</span>{" "}
                  <span className="capitalize">{lvl}</span>
                </li>
              ))}
            </ul>
          )}
          {interiorIssues.length > 0 && (
            <div className="text-xs text-[var(--ssc-navy)] mt-1">
              <span className="font-bold">Interior:</span> {interiorIssues.join(", ")}
            </div>
          )}
        </div>
      )}

      {(detected.length > 0 || confirmed.length > 0) && (
        <div className="mt-2 rounded-xl border border-[#D9E1EA] bg-white p-3" data-testid="lead-trade-options">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] mb-1">
            {detected.length > 0 ? "Detected Factory Options" : "Customer-Confirmed Options"}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {detected.length > 0 ? (
              detected.map((o) => {
                const isConfirmed = confirmed.includes(o);
                return (
                  <span key={o}
                    className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                      isConfirmed
                        ? "bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] border-[var(--ssc-blue)]/30"
                        : "bg-[#F1F5F9] text-[#6b7c93] border-[#D9E1EA] line-through opacity-70"
                    }`}>
                    {o}
                  </span>
                );
              })
            ) : (
              confirmed.map((o) => (
                <span key={o}
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full border bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] border-[var(--ssc-blue)]/30">
                  {o}
                </span>
              ))
            )}
          </div>
          {detected.length > 0 && (
            <div className="text-[10px] text-[#6b7c93] mt-1.5">Crossed-out = customer says it's no longer on the vehicle.</div>
          )}
        </div>
      )}

      {est && est.dealer_trade_high && (
        <div className="mt-2 rounded-xl border border-[#D9E1EA] bg-white p-3 text-xs" data-testid="lead-trade-estimate">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-700 mb-1">
            Customer's Estimate
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {est.dealer_trade_low && est.dealer_trade_high && (
              <div><span className="font-bold text-[#6b7c93]">Trade range:</span> ${Number(est.dealer_trade_low).toLocaleString()} – ${Number(est.dealer_trade_high).toLocaleString()}</div>
            )}
            {est.instant_cash && <div><span className="font-bold text-[#6b7c93]">Instant cash:</span> ${Number(est.instant_cash).toLocaleString()}</div>}
            {est.wholesale_value && <div><span className="font-bold text-[#6b7c93]">Wholesale:</span> ${Number(est.wholesale_value).toLocaleString()}</div>}
            {est.recon_cost != null && <div><span className="font-bold text-[#6b7c93]">Recon est:</span> ${Number(est.recon_cost).toLocaleString()}</div>}
            {est.confidence && <div><span className="font-bold text-[#6b7c93]">Confidence:</span> {est.confidence}%</div>}
            {est.demand_rating && <div><span className="font-bold text-[#6b7c93]">Demand:</span> {est.demand_rating}</div>}
          </div>
        </div>
      )}
    </section>
  );
}

function DLGrid({ items }) {
  const rows = items.filter(([, v]) => v != null && v !== "");
  if (rows.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
      {rows.map(([k, v]) => (
        <div key={k}>
          <span className="text-[#6b7c93] font-bold">{k}:</span>{" "}
          <span className="text-[var(--ssc-navy)] font-semibold">{v}</span>
        </div>
      ))}
    </div>
  );
}


function ProtectionSummary({ lead }) {
  const selected = lead?.protection_selected || [];
  const declined = lead?.protection_declined || [];
  const interactions = lead?.protection_interactions || [];
  const total = lead?.protection_total || selected.reduce((s, p) => s + (p.price || 0), 0);
  const secondChanceAccepts = interactions.filter((i) => i.action === "second-chance-accepted");

  return (
    <section className="p-4 border-b border-[#D9E1EA]" data-testid="lead-protection-summary">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-xs font-black uppercase tracking-wider text-[var(--ssc-blue)]">
          Protection Products
        </div>
        {total > 0 && (
          <div className="text-sm font-black text-[var(--ssc-navy)]" data-testid="lead-protection-total">
            ${total.toLocaleString()} priced
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Added</div>
          <ul className="space-y-1">
            {selected.map((p) => (
              <li key={p.slug} className="flex items-center justify-between text-xs"
                  data-testid={`lead-protection-added-${p.slug}`}>
                <span className="font-bold text-[var(--ssc-navy)]">
                  {p.name}
                  {p.acceptedSecondChance && (
                    <span className="ml-1 inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[9px] font-bold uppercase">
                      2nd-chance
                    </span>
                  )}
                </span>
                {p.price != null && (
                  <span className="font-bold text-[var(--ssc-navy)]">${(p.price || 0).toLocaleString()}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {declined.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#6b7c93] mb-1">Declined</div>
          <div className="text-xs text-[var(--ssc-ink)]" data-testid="lead-protection-declined">
            {declined.join(", ")}
          </div>
        </div>
      )}

      {secondChanceAccepts.length > 0 && (
        <div className="text-[11px] text-emerald-700 font-bold mt-2"
             data-testid="lead-protection-second-chance">
          ⚡ {secondChanceAccepts.length} second-chance offer{secondChanceAccepts.length > 1 ? "s" : ""} accepted
        </div>
      )}

      {interactions.length > 0 && (
        <details className="mt-2">
          <summary className="text-[11px] text-[#6b7c93] cursor-pointer hover:text-[var(--ssc-navy)]">
            Interaction log ({interactions.length})
          </summary>
          <ul className="mt-1 space-y-0.5 text-[11px] text-[#6b7c93]">
            {interactions.map((i, idx) => (
              <li key={idx}>
                {new Date(i.at).toLocaleTimeString()} · {i.slug} · {i.action}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
